import {
  calculateTaxOnIncome,
  STANDARD_DEDUCTIONS,
  TAX_BRACKETS,
  STATE_TAX_DATA,
} from '../calculations'

// Long-term capital gains brackets (2025)
const LTCG_BRACKETS: Record<string, Array<{ min: number; max: number; rate: number }>> = {
  single: [
    { min: 0, max: 48350, rate: 0 },
    { min: 48350, max: 533400, rate: 15 },
    { min: 533400, max: Infinity, rate: 20 },
  ],
  mfj: [
    { min: 0, max: 96700, rate: 0 },
    { min: 96700, max: 600050, rate: 15 },
    { min: 600050, max: Infinity, rate: 20 },
  ],
  hoh: [
    { min: 0, max: 64750, rate: 0 },
    { min: 64750, max: 566700, rate: 15 },
    { min: 566700, max: Infinity, rate: 20 },
  ],
}

// NIIT thresholds
const NIIT_THRESHOLDS: Record<string, number> = {
  single: 200000,
  mfj: 250000,
  hoh: 200000,
}

interface CapitalGainsInput {
  purchasePrice: number
  salePrice: number
  holdingPeriodMonths: number
  filingStatus: string
  otherIncome: number
  stateCode: string
  customStateTaxRate?: number
}

function calculateLTCGTax(taxableIncome: number, gain: number, filingStatus: string): number {
  const brackets = LTCG_BRACKETS[filingStatus] || LTCG_BRACKETS.single
  // The LTCG stacks on top of ordinary income
  let tax = 0
  let remaining = gain
  let incomeFloor = taxableIncome // ordinary income fills brackets first

  for (const bracket of brackets) {
    if (incomeFloor >= bracket.max) continue
    const bracketStart = Math.max(bracket.min, incomeFloor)
    const bracketSpace = bracket.max - bracketStart
    const taxableInBracket = Math.min(remaining, bracketSpace)
    if (taxableInBracket <= 0) continue
    tax += taxableInBracket * (bracket.rate / 100)
    remaining -= taxableInBracket
    incomeFloor = bracketStart + taxableInBracket
    if (remaining <= 0) break
  }

  return Math.round(tax * 100) / 100
}

export function calculateCapitalGains(input: CapitalGainsInput) {
  const gain = Math.max(0, input.salePrice - input.purchasePrice)
  const loss = Math.max(0, input.purchasePrice - input.salePrice)
  const isLongTerm = input.holdingPeriodMonths >= 12

  const deduction = STANDARD_DEDUCTIONS[input.filingStatus] || STANDARD_DEDUCTIONS.single
  const ordinaryTaxableIncome = Math.max(0, input.otherIncome - deduction)

  // State tax
  const stateEntry = STATE_TAX_DATA.find(s => s.code === input.stateCode)
  const stateRate = stateEntry?.code === 'CUSTOM'
    ? (input.customStateTaxRate || 0)
    : (stateEntry?.rate || 0)

  // Short-term: taxed at ordinary income rates
  const shortTermTaxableIncome = ordinaryTaxableIncome + gain
  const shortTermFederalTax = calculateTaxOnIncome(shortTermTaxableIncome, input.filingStatus) -
    calculateTaxOnIncome(ordinaryTaxableIncome, input.filingStatus)
  const shortTermStateTax = gain * (stateRate / 100)

  // Short-term NIIT
  const niitThreshold = NIIT_THRESHOLDS[input.filingStatus] || 200000
  const shortTermAGI = input.otherIncome + gain
  const shortTermNIIT = shortTermAGI > niitThreshold
    ? Math.min(gain, shortTermAGI - niitThreshold) * 0.038
    : 0
  const shortTermTotalTax = shortTermFederalTax + shortTermStateTax + shortTermNIIT

  // Long-term: preferential rates
  const longTermFederalTax = calculateLTCGTax(ordinaryTaxableIncome, gain, input.filingStatus)
  const longTermStateTax = gain * (stateRate / 100) // most states tax LTCG at ordinary rates

  // Long-term NIIT
  const longTermAGI = input.otherIncome + gain
  const longTermNIIT = longTermAGI > niitThreshold
    ? Math.min(gain, longTermAGI - niitThreshold) * 0.038
    : 0
  const longTermTotalTax = longTermFederalTax + longTermStateTax + longTermNIIT

  // Actual tax based on holding period
  const actualFederalTax = isLongTerm ? longTermFederalTax : shortTermFederalTax
  const actualStateTax = isLongTerm ? longTermStateTax : shortTermStateTax
  const actualNIIT = isLongTerm ? longTermNIIT : shortTermNIIT
  const actualTotalTax = isLongTerm ? longTermTotalTax : shortTermTotalTax

  const netProceeds = input.salePrice - actualTotalTax
  const effectiveRate = gain > 0 ? (actualTotalTax / gain) * 100 : 0
  const savings = shortTermTotalTax - longTermTotalTax

  return {
    purchasePrice: input.purchasePrice,
    salePrice: input.salePrice,
    gain: Math.round(gain),
    loss: Math.round(loss),
    isLongTerm,
    holdingPeriodMonths: input.holdingPeriodMonths,
    // Short-term breakdown
    shortTermFederalTax: Math.round(shortTermFederalTax),
    shortTermStateTax: Math.round(shortTermStateTax),
    shortTermNIIT: Math.round(shortTermNIIT),
    shortTermTotalTax: Math.round(shortTermTotalTax),
    // Long-term breakdown
    longTermFederalTax: Math.round(longTermFederalTax),
    longTermStateTax: Math.round(longTermStateTax),
    longTermNIIT: Math.round(longTermNIIT),
    longTermTotalTax: Math.round(longTermTotalTax),
    // Actual (based on holding period)
    actualFederalTax: Math.round(actualFederalTax),
    actualStateTax: Math.round(actualStateTax),
    actualNIIT: Math.round(actualNIIT),
    actualTotalTax: Math.round(actualTotalTax),
    netProceeds: Math.round(netProceeds),
    effectiveRate: Math.round(effectiveRate * 10) / 10,
    savings: Math.round(savings),
    stateRate,
  }
}
